import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { VEHICLE_TYPES } from "@/lib/constants";

const EMPTY = { plaque: "", type: "Ambulance", agrement_ars: "", depot: "Siège", lat: 47.322, lon: 5.041, ct_date: "", desinfection_date: "", extincteur_date: "", km: 0, active: true };

export default function Vehicles() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/vehicles").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  // Ouverture directe de la fiche via lien cliquable (?focus=<id>).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const focus = searchParams.get("focus");
    if (focus && items.length) {
      const v = items.find((x) => x.id === focus);
      if (v) { openEdit(v); setSearchParams({}, { replace: true }); }
    }
    /* eslint-disable-next-line */
  }, [items, searchParams]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (v) => { setForm({ ...EMPTY, ...v }); setEditId(v.id); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      if (editId) await api.put(`/vehicles/${editId}`, form);
      else await api.post("/vehicles", form);
      toast.success("Véhicule enregistré");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => { await api.delete(`/vehicles/${id}`); load(); };

  const ctExpiring = (d) => d && new Date(d) < new Date(Date.now() + 30 * 86400000);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Véhicules</h1>
          <p className="text-muted-foreground text-sm">Flotte, conformité réglementaire & facturation SaaS.</p>
        </div>
        <Button onClick={openNew} data-testid="vehicles-add-button" className="gap-2"><Plus className="h-4 w-4" /> Ajouter</Button>
      </div>

      <Card className="p-0 rounded-md shadow-none overflow-hidden" data-testid="vehicles-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Plaque</TableHead><TableHead>Type</TableHead><TableHead>Agrément ARS</TableHead>
              <TableHead>Dépôt</TableHead><TableHead>CT</TableHead><TableHead>Km</TableHead><TableHead>Actif</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Aucun véhicule.</TableCell></TableRow>}
            {items.map((v) => (
              <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`}>
                <TableCell className="font-medium">{v.plaque}</TableCell>
                <TableCell><Badge variant={v.type === "Taxi" ? "default" : "outline"} className={v.type === "Taxi" ? "bg-accent text-accent-foreground" : ""}>{v.type}</Badge></TableCell>
                <TableCell className="text-sm text-muted-foreground">{v.agrement_ars || "—"}</TableCell>
                <TableCell className="text-sm">{v.depot}</TableCell>
                <TableCell className="text-sm">
                  {v.ct_date ? (
                    <span className={`inline-flex items-center gap-1 ${ctExpiring(v.ct_date) ? "text-destructive" : ""}`}>
                      {ctExpiring(v.ct_date) && <AlertTriangle className="h-3 w-3" />}{new Date(v.ct_date).toLocaleDateString("fr-FR")}
                    </span>
                  ) : "—"}
                </TableCell>
                <TableCell className="text-sm">{v.km?.toLocaleString("fr-FR")}</TableCell>
                <TableCell>{v.active ? <Badge variant="outline" className="border-green-500 text-green-600">Oui</Badge> : <Badge variant="outline">Non</Badge>}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(v)} data-testid={`vehicle-edit-${v.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(v.id)} data-testid={`vehicle-delete-${v.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Modifier" : "Nouveau"} véhicule</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Formulaire de saisie des informations du véhicule.</DialogDescription>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Plaque</Label><Input value={form.plaque} onChange={(e) => set("plaque", e.target.value)} className="mt-1" data-testid="vehicle-plaque" /></div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="mt-1" data-testid="vehicle-type"><SelectValue /></SelectTrigger>
                <SelectContent>{VEHICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Agrément ARS</Label><Input value={form.agrement_ars} onChange={(e) => set("agrement_ars", e.target.value)} className="mt-1" /></div>
            <div><Label>Dépôt</Label><Input value={form.depot} onChange={(e) => set("depot", e.target.value)} className="mt-1" /></div>
            <div><Label>Contrôle technique</Label><Input type="date" value={form.ct_date || ""} onChange={(e) => set("ct_date", e.target.value)} className="mt-1" /></div>
            <div><Label>Extincteur</Label><Input type="date" value={form.extincteur_date || ""} onChange={(e) => set("extincteur_date", e.target.value)} className="mt-1" /></div>
            <div><Label>Désinfection</Label><Input type="date" value={form.desinfection_date || ""} onChange={(e) => set("desinfection_date", e.target.value)} className="mt-1" /></div>
            <div><Label>Kilométrage</Label><Input type="number" value={form.km} onChange={(e) => set("km", Number(e.target.value))} className="mt-1" /></div>
            <div className="col-span-2 flex items-center gap-2 pt-1">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="vehicle-active" />
              <Label>Véhicule actif (compte dans la facturation)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} data-testid="vehicle-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
